<script setup lang="ts">
/**
 * Marketing tab (T07) — UTM attribution + promo codes (static content).
 *
 * Layout (matches reference/img_9.png bottom half):
 *   1. Intro paragraph about first-touch attribution.
 *   2. Supported URL parameters table (utm_source / utm_medium / …).
 *   3. Promo codes section.
 *
 * No dynamic data; copy is sourced verbatim from the reference image.
 */
import CopyCodeBlock from './CopyCodeBlock.vue';

interface UtmRow {
  parameter: string;
  description: string;
  example: string;
}

const UTM_ROWS: UtmRow[] = [
  { parameter: 'utm_source', description: 'Traffic source', example: 'telegram' },
  { parameter: 'utm_medium', description: 'Channel type', example: 'post' },
  { parameter: 'utm_campaign', description: 'Campaign name', example: 'kimi_launch' },
  { parameter: 'utm_content', description: 'Creative / placement', example: 'button_top' },
  { parameter: 'utm_term', description: 'Optional keyword', example: 'cheap_inference' },
  { parameter: 'ref', description: 'Free-form referrer tag', example: 'partner_a' },
  { parameter: 'promo', description: 'Promo / coupon code', example: 'LAUNCH50' },
];

const EXAMPLE_URL = `https://opengonka.com/?utm_source=telegram&utm_medium=post&utm_campaign=kimi_launch&utm_content=button_top&promo=LAUNCH50`;
</script>

<template>
  <div class="flex flex-col gap-6">
    <section class="card p-6">
      <h2 class="text-lg font-semibold">Marketing Attribution, UTM &amp; Promo Codes</h2>
      <p class="text-sm text-muted mt-2">
        opengonka.com captures first-touch attribution from URL parameters and stores it on
        registration. Use this for influencer campaigns, Telegram posts, newsletter links, and
        partner channels.
      </p>

      <h3 class="text-base font-semibold mt-6">Supported URL parameters</h3>
      <div class="mt-3 overflow-x-auto">
        <table class="table w-full">
          <thead>
            <tr>
              <th class="w-[200px]">Parameter</th>
              <th>Description</th>
              <th class="w-[220px]">Example</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in UTM_ROWS" :key="row.parameter">
              <td><code class="text-ink">{{ row.parameter }}</code></td>
              <td class="text-sm">{{ row.description }}</td>
              <td><code>{{ row.example }}</code></td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3 class="text-base font-semibold mt-6">Example tracked URL</h3>
      <div class="mt-3">
        <CopyCodeBlock :code="EXAMPLE_URL" />
      </div>
      <p class="text-xs text-muted mt-3">
        Attribution is stored once, on the first request that establishes a session. Subsequent
        visits don't overwrite it, so the original campaign keeps credit for the user's lifetime
        spend.
      </p>
    </section>

    <section class="card p-6">
      <h2 class="text-lg font-semibold">Promo Codes</h2>
      <p class="text-sm text-muted mt-2">
        Promo codes apply a one-shot bonus or rebate on top of the default welcome bonus. Pass
        the code via the <code>promo</code> URL parameter (preferred — survives sign-up flow) or
        enter it manually on the <router-link to="/billing" class="text-accent hover:underline">Billing</router-link>
        page after registration.
      </p>

      <ul class="mt-4 space-y-3 text-sm">
        <li class="flex items-start gap-3">
          <span class="text-[11px] font-mono px-2 py-0.5 rounded bg-surface-soft text-ok">LAUNCH50</span>
          <span class="text-muted">+50% bonus on first top-up (max 10 GNK).</span>
        </li>
        <li class="flex items-start gap-3">
          <span class="text-[11px] font-mono px-2 py-0.5 rounded pill">DEV2026</span>
          <span class="text-muted">Extra 5,000,000 free tokens for developer signups.</span>
        </li>
        <li class="flex items-start gap-3">
          <span class="text-[11px] font-mono px-2 py-0.5 rounded pill">PARTNER</span>
          <span class="text-muted">Reserved prefix for partner-program codes; contact us for issuance.</span>
        </li>
      </ul>

      <p class="text-xs text-muted mt-4">
        Codes are case-insensitive, single-use per account, and expire 30 days after issuance
        unless otherwise noted in the campaign brief.
      </p>
    </section>
  </div>
</template>
